/**
 * MRR movement — month-over-month diff of two MRR snapshot sets.
 *
 * Pure (no I/O): the caller loads the prior + current month rows from
 * `mrr_snapshots` and passes them here. Movement is keyed by `source:account_id`
 * so the two revenue streams (recurring_transaction, won_deal) never collide:
 *
 *   new         — account present this month, absent last month
 *   churned     — account present last month, absent this month (lost MRR)
 *   expansion   — account in both, amount increased
 *   contraction — account in both, amount decreased
 *   netChange   — Σ current − Σ prior
 */

export interface MrrSnapshotRow {
  source: string;
  account_id: string;
  amount: number;
}

export interface MrrMovement {
  priorMrr: number;
  currentMrr: number;
  netChange: number;
  newMrr: number;
  expansionMrr: number;
  /** Negative — MRR lost to downgrades. */
  contractionMrr: number;
  /** Negative — MRR lost to churn. */
  churnedMrr: number;
  newCount: number;
  churnedCount: number;
}

const num = (v: number | null | undefined): number =>
  typeof v === "number" && Number.isFinite(v) ? v : 0;

function keyOf(row: MrrSnapshotRow): string {
  return `${row.source}:${row.account_id}`;
}

function sumByKey(rows: MrrSnapshotRow[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of rows) {
    map.set(keyOf(row), (map.get(keyOf(row)) ?? 0) + num(row.amount));
  }
  return map;
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

export function computeMrrMovement(
  prior: MrrSnapshotRow[],
  current: MrrSnapshotRow[]
): MrrMovement {
  const priorMap = sumByKey(prior);
  const currentMap = sumByKey(current);

  let newMrr = 0;
  let expansionMrr = 0;
  let contractionMrr = 0;
  let churnedMrr = 0;
  let newCount = 0;
  let churnedCount = 0;

  for (const [key, curAmount] of currentMap) {
    const priorAmount = priorMap.get(key);
    if (priorAmount === undefined) {
      newMrr += curAmount;
      newCount += 1;
    } else if (curAmount > priorAmount) {
      expansionMrr += curAmount - priorAmount;
    } else if (curAmount < priorAmount) {
      contractionMrr += curAmount - priorAmount; // negative
    }
  }

  for (const [key, priorAmount] of priorMap) {
    if (!currentMap.has(key)) {
      churnedMrr -= priorAmount; // negative
      churnedCount += 1;
    }
  }

  const priorMrr = Array.from(priorMap.values()).reduce((s, v) => s + v, 0);
  const currentMrr = Array.from(currentMap.values()).reduce((s, v) => s + v, 0);

  return {
    priorMrr: round(priorMrr),
    currentMrr: round(currentMrr),
    netChange: round(currentMrr - priorMrr),
    newMrr: round(newMrr),
    expansionMrr: round(expansionMrr),
    contractionMrr: round(contractionMrr),
    churnedMrr: round(churnedMrr),
    newCount,
    churnedCount,
  };
}
